import type { RuntimeConfig } from "./config";

interface CognitoAuthResult {
  readonly IdToken: string;
  readonly AccessToken: string;
  readonly RefreshToken: string;
  readonly ExpiresIn: number;
}

export interface Session {
  readonly idToken: string;
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly expiresAt: number;
}

const STORAGE_KEY = "kindle-papers.session";

const cognitoEndpoint = (region: string) => `https://cognito-idp.${region}.amazonaws.com/`;

const cognitoCall = async <T>(region: string, target: string, body: unknown): Promise<T> => {
  const response = await fetch(cognitoEndpoint(region), {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  });
  const payload = (await response.json()) as T & { message?: string; __type?: string };
  if (!response.ok) {
    throw new Error(payload.message ?? payload.__type ?? "Cognito request failed");
  }
  return payload;
};

export const loadSession = (): Session | null => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Session;
  } catch {
    return null;
  }
};

export const saveSession = (session: Session): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
};

export const clearSession = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

export const signIn = async (
  config: RuntimeConfig,
  email: string,
  password: string,
): Promise<Session> => {
  const result = await cognitoCall<{ AuthenticationResult?: CognitoAuthResult; ChallengeName?: string }>(
    config.cognitoRegion,
    "InitiateAuth",
    {
      AuthFlow: "USER_PASSWORD_AUTH",
      ClientId: config.cognitoUserPoolClientId,
      AuthParameters: {
        USERNAME: email,
        PASSWORD: password,
      },
    },
  );
  if (!result.AuthenticationResult?.IdToken) {
    throw new Error(result.ChallengeName ? `Challenge required: ${result.ChallengeName}` : "Sign-in failed");
  }
  const session: Session = {
    idToken: result.AuthenticationResult.IdToken,
    accessToken: result.AuthenticationResult.AccessToken,
    refreshToken: result.AuthenticationResult.RefreshToken,
    expiresAt: Date.now() + result.AuthenticationResult.ExpiresIn * 1000,
  };
  saveSession(session);
  return session;
};

export const refreshSession = async (config: RuntimeConfig, session: Session): Promise<Session> => {
  const result = await cognitoCall<{ AuthenticationResult?: CognitoAuthResult }>(
    config.cognitoRegion,
    "InitiateAuth",
    {
      AuthFlow: "REFRESH_TOKEN_AUTH",
      ClientId: config.cognitoUserPoolClientId,
      AuthParameters: {
        REFRESH_TOKEN: session.refreshToken,
      },
    },
  );
  if (!result.AuthenticationResult?.IdToken) throw new Error("Could not refresh session");
  const next: Session = {
    idToken: result.AuthenticationResult.IdToken,
    accessToken: result.AuthenticationResult.AccessToken ?? session.accessToken,
    refreshToken: session.refreshToken,
    expiresAt: Date.now() + (result.AuthenticationResult.ExpiresIn ?? 3600) * 1000,
  };
  saveSession(next);
  return next;
};

export const ensureFreshSession = async (config: RuntimeConfig): Promise<Session | null> => {
  const session = loadSession();
  if (!session) return null;
  if (session.expiresAt > Date.now() + 60_000) return session;
  try {
    return await refreshSession(config, session);
  } catch {
    clearSession();
    return null;
  }
};
